import { lazy, Suspense, useState, type MutableRefObject } from "react";
import type { DashboardData, ExpenseInput, Owner, Transaction } from "../../types";
import type { PriceObservation } from "../../types/receipt";
import type { EditJournal } from "./edit-save";
import type { ConfirmState } from "../../components/ConfirmDialog";
import { dateLabel, dateTimeLabel, isExpense, isIncome, markupAmount, money, shortName } from "../../lib/finance";
import { categoryIcon } from "../../components/CategoryIcon";
import { categoryLabel } from "../../lib/allocations";
import { useStartTransactionEdit } from "./use-start-transaction-edit";
import MakeTransactionRecurring from "../bills/MakeTransactionRecurring";
import ReceiptDetails from "../receipts/ReceiptDetails";
import TransactionTrackedPrices from "./TransactionTrackedPrices";
import LoadingLabel from "../../components/LoadingLabel";
import Modal from "../../components/Modal";
import { ArrowRight, Pencil, Repeat, Trash2 } from "lucide-react";

const TransactionConversionDetails = lazy(() => import("./TransactionConversionDetails"));
const TransactionEditForm = lazy(() => import("./TransactionEditForm"));

// One transaction, read or edited. Editing is not a simple form post: a save can touch the
// transaction and several tracked prices, so it runs through a journal that survives a reload and
// resumes where it stopped. That machinery is handed in rather than owned here, because App also
// needs it to decide whether closing the sheet would abandon a half-finished save.

export interface TransactionDetailProps {
  detail: Transaction;
  data: DashboardData | null;
  profile: Owner | null;
  editingDetail: boolean;
  setEditingDetail: (editing: boolean) => void;
  detailBusy: boolean;
  detailError: string;
  setDetailError: (message: string) => void;
  setDetailBusy: (busy: boolean) => void;
  setDetailPrices: (rows: PriceObservation[]) => void;
  refresh: (force?: boolean) => Promise<unknown>;
  /** Where this transaction's in-progress edit is kept on the device. */
  editKey: (id: string) => string;
  detailPrices: PriceObservation[];
  /** Survives a reload, so an interrupted save can be resumed rather than repeated. */
  editJournal: MutableRefObject<EditJournal | null>;
  editPriceSnapshot: MutableRefObject<PriceObservation[]>;
  persistEdit: (value: EditJournal) => void;
  transactionDraft: (t: Transaction) => ExpenseInput;
  updateDetail: (expense: ExpenseInput) => void;
  askDeleteDetail: () => void;
  setDetail: (t: Transaction | null) => void;
  setConfirm: (state: ConfirmState | null) => void;
  onOpenIou: (id: string) => void;
  onOpenBill: (id: string) => void;
  onOpenGoal?: (id: string) => void;
  onOpenTrip?: (id: string) => void;
}

export default function TransactionDetail({
  detail, data, profile, editingDetail, setEditingDetail, detailBusy, detailError,
  setDetailError, setDetailBusy, setDetailPrices, refresh, editKey,
  detailPrices, editJournal, editPriceSnapshot, persistEdit,
  transactionDraft, updateDetail, askDeleteDetail, setDetail, setConfirm, onOpenIou, onOpenBill, onOpenTrip, onOpenGoal,
}: TransactionDetailProps) {
  const canEdit = isExpense(detail) || isIncome(detail) || !!detail.toAccountId;
  const canDelete = detail.id.startsWith("TXN-") || !!detail.toAccountId;
  // A loan leg or a transfer isn't an ordinary recurring payment, and a transaction already linked
  // to one bill can't anchor a second — matches the same exclusions the Bills screen's own
  // "base this on an existing transaction" picker applies.
  const canMakeRecurring = (isExpense(detail) || isIncome(detail)) && !detail.iouId && !detail.billId && !detail.currencyConversion && !detail.allocations;
  const [makingRecurring, setMakingRecurring] = useState(false);
  const { startEdit, clearing, receipts, notice } = useStartTransactionEdit({
    detail, data, setDetailBusy, setDetailPrices, editKey, editPriceSnapshot,
    persistEdit, setDetailError, setEditingDetail,
  });
  return (
    <>
    <Modal
      title={editingDetail ? "Edit transaction" : "Transaction details"}
      onClose={() => {
        if (detailBusy) return;
        if (editingDetail) {
          setConfirm({ title: "Leave transaction editor?", message: "Your draft and save progress stay on this device. Open Edit again to continue.", confirmLabel: "Leave editor", onConfirm: () => { setEditingDetail(false); setDetail(null); } });
          return;
        }
        setDetail(null);
        setEditingDetail(false);
        setDetailError("");
      }}
      headerActions={!editingDetail && (
        <>
          {canEdit && (
            <button className="icon-button" disabled={detailBusy} onClick={() => void startEdit()} aria-label="Edit transaction">
              <Pencil size={18} />
            </button>
          )}
          {canMakeRecurring && (
            <button className="icon-button" disabled={detailBusy} onClick={() => setMakingRecurring(true)} aria-label={isIncome(detail) ? "Make this recurring income" : "Make this a recurring bill"}>
              <Repeat size={18} />
            </button>
          )}
          {canDelete && (
            <button className="icon-button danger-icon-button" disabled={detailBusy} onClick={askDeleteDetail} aria-label="Delete transaction">
              <Trash2 size={18} />
            </button>
          )}
        </>
      )}
    >
      {editingDetail && data ? (
        <Suspense fallback={<p role="status">Loading transaction editor…</p>}><TransactionEditForm
          allowConversion={!detail.billId}
          editingTransactionId={detail.id}
          journal={editJournal} persist={persistEdit} receipts={receipts} notice={notice}
          key={detail.id}
          data={data}
          profile={profile || detail.owner}
          initialInput={editJournal.current?.expense || { ...transactionDraft(detail), ...(clearing ? { pending: false } : {}), ...(detail.needsReview ? { needsReview: false } : {}) }}
          loanTransaction={!!detail.iouId}
          clearing={clearing}
          onDraftChange={expense => { if (!editJournal.current?.started) { try { persistEdit({ ...editJournal.current, transaction: detail, originalPrices: editPriceSnapshot.current, expense, started: false, transactionSaved: false, completed: [] }); } catch { setDetailError("Cannot retain your draft on this device. Save will not start until storage is available."); } } }}
          allowTracking={isExpense(detail)}
          externalBusy={detailBusy}
          freezeFields={!!editJournal.current?.started}
          reviewLabel={detailBusy ? <LoadingLabel>Saving</LoadingLabel> : editJournal.current?.started ? "Retry remaining changes" : "Save changes"}
          autoFocusAmount={false}
          onSaved={() => {}}
          onReview={(expense) => void updateDetail(expense)}
        /></Suspense>
      ) : (
        <>
          <div className="detail-hero">
            <span className="transaction-icon">
              {(() => {
                const I = categoryIcon(detail.category);
                return <I size={26} />;
              })()}
            </span>
            <h3>
              {detail.title || detail.description || categoryLabel(detail)}
            </h3>
            <strong className={isIncome(detail) ? "income" : ""}>
              {isExpense(detail) ? "−" : isIncome(detail) ? "+" : ""}
              {money(detail.amount, detail.currency)}
            </strong>
            <p>
              {dateLabel(detail.date)}
              {detail.date.slice(11, 19) !== "00:00:00"
                ? " · " + detail.date.slice(11, 19)
                : detail.time
                  ? " · " + detail.time
                  : ""}
            </p>
          </div>
          {detail.needsReview ? (
            <div className="pending-notice" role="status">
              <p>
                <strong>Imported from SMS — Needs review.</strong>{" "}
                {detail.smsRef ? `Reference: ${detail.smsRef}. ` : ""}
                Review category, title and split, then approve.
              </p>
              <div className="detail-actions">
                <button
                  type="button"
                  disabled={detailBusy}
                  onClick={() =>
                    void updateDetail({
                      ...transactionDraft(detail),
                      needsReview: false,
                    })
                  }
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  disabled={detailBusy}
                  onClick={() => void startEdit(false)}
                >
                  Edit details
                </button>
                {canDelete && (
                  <button type="button" className="danger-button" disabled={detailBusy} onClick={askDeleteDetail}>
                    Void
                  </button>
                )}
              </div>
            </div>
          ) : detail.pending ? (
            <div className="pending-notice" role="status">
              <p>
                <strong>Pending at the bank.</strong>{" "}
                It counts in your balance now. Mark it cleared once the bank settles it, or void it if it never does.
              </p>
              <div className="detail-actions">
                <button type="button" disabled={detailBusy} onClick={() => void startEdit(true)}>
                  Mark cleared
                </button>
                {canDelete && (
                  <button type="button" className="danger-button" disabled={detailBusy} onClick={askDeleteDetail}>
                    Void
                  </button>
                )}
              </div>
            </div>
          ) : null}
          {detail.currencyConversion && <Suspense fallback={<p>Loading exchange details…</p>}><TransactionConversionDetails transaction={detail} onOpen={setDetail} onEdit={() => void startEdit()} /></Suspense>}
          <dl className="detail-list">
            {([
              ["Type", detail.type],
              [
                "Status",
                detail.needsReview
                  ? detail.pending
                    ? "Needs review · Bank hold"
                    : "Needs review"
                  : detail.pending
                    ? "Pending at bank"
                    : "",
              ],
              ...(detail.smsRef ? [["SMS Reference", detail.smsRef]] : []),
              ["Profile", detail.owner],
              [
                "Split",
                detail.split
                  ? detail.split.type === "joint"
                    ? "Joint (Shared)"
                    : detail.split.type === "equal"
                      ? "50/50 Split"
                      : detail.split.type === "personal"
                        ? `Personal (${detail.split.participants?.[0] || detail.owner})`
                        : detail.split.shares
                          ? `Custom (${Object.entries(detail.split.shares)
                              .map(([o, s]) => `${o}: ${money(s, detail.currency)}`)
                              .join(", ")})`
                          : "Custom pool"
                  : "",
              ],
              [
                "Settlement",
                detail.isSettlement ? "Partner debt settlement" : "",
              ],
              [
                "Account",
                shortName(
                  data?.accounts.find((a) => a.id === detail.accountId)
                    ?.name || detail.accountId,
                ),
              ],
              [
                "To account",
                detail.toAccountId
                  ? shortName(
                      data?.accounts.find(
                        (a) => a.id === detail.toAccountId,
                      )?.name || detail.toAccountId,
                    )
                  : "",
              ],
              [
                "Received",
                detail.receivedAmount !== undefined
                  ? money(detail.receivedAmount, detail.receivedCurrency)
                  : "",
              ],
              [
                "Exchange rate",
                detail.receivedAmount && detail.amount && detail.receivedCurrency &&
                new Set([detail.currency, detail.receivedCurrency]).size === 2 &&
                [detail.currency, detail.receivedCurrency].sort().join() === "MVR,USD"
                  ? `1 USD = ${(Math.round((detail.currency === "USD" ? detail.receivedAmount / detail.amount : detail.amount / detail.receivedAmount) * 1e4) / 1e4)} MVR`
                  : "",
              ],
              [
                "Transfer fee",
                detail.fee ? money(detail.fee, detail.currency) : "",
              ],
              ["Category", detail.allocations ? "" : detail.category],
              ["Subcategory", detail.allocations ? "" : detail.subcategory],
              [
                detail.draftPayeeName ? "Payee (draft)" : "Payee",
                data?.payees?.find((p) => p.id === detail.payeeId)?.name || detail.draftPayeeName || "",
              ],
              [
                "Loan",
                data?.ious?.find((i) => i.id === detail.iouId)?.description || "",
                detail.iouId,
                onOpenIou,
              ],
              [
                data?.recurring?.find((r) => r.id === detail.billId)?.type === "Income" ? "Recurring income" : "Bill",
                data?.recurring?.find((r) => r.id === detail.billId)?.name || "",
                detail.billId,
                onOpenBill,
              ],
              ["Goal", detail.goalId ? data?.goals?.find(goal => goal.id === detail.goalId)?.name || "Linked goal" : "", detail.goalId, onOpenGoal],
              [
                "Trip",
                data?.trips?.find((t) => t.id === detail.tripId)?.name || "",
                detail.tripId,
                onOpenTrip,
              ],
              [
                "Trip leg",
                detail.tripDestination || "",
              ],
              [
                "Reportable amount",
                detail.reportableAmount !== detail.amount
                  ? money(detail.reportableAmount, detail.currency)
                  : "",
              ],
              [
                "Description",
                detail.description && detail.description.trim() !== (detail.title || "").trim()
                  ? detail.description
                  : "",
              ],
              [
                "FX / Markup",
                (() => {
                  if (!detail.markup) return "";
                  const fee = markupAmount(detail.amount, detail.markup);
                  return fee !== null ? `${detail.markup} (+${money(fee, detail.currency)})` : detail.markup;
                })(),
              ],
              ["Notes", detail.notes],
              ["Added by", detail.addedBy],
              [
                "Added",
                detail.createdAt
                  ? dateTimeLabel(detail.createdAt, data?.timezone)
                  : "",
              ],
              ["Transaction ID", detail.id],
            ] as [string, string, string?, ((id: string) => void)?][])
              .filter(([, v]) => v)
              .map(([k, v, id, onOpen]) => (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd>
                    {id && onOpen ? (
                      <button type="button" className="detail-link" onClick={() => onOpen(id)}>
                        {v} <ArrowRight size={14} />
                      </button>
                    ) : (
                      v
                    )}
                  </dd>
                </div>
              ))}
          </dl>
          {detail.allocations && <section className="detail-splits" aria-label="Category breakdown"><h3>Category breakdown</h3><dl className="detail-list">{detail.allocations.map((part, index) => <div key={index}><dt>{part.category}{part.subcategory ? ` · ${part.subcategory}` : ""}</dt><dd>{money(part.amount, detail.currency)}{part.reportableAmount !== part.amount && <small>Household: {money(part.reportableAmount, detail.currency)}</small>}</dd></div>)}</dl></section>}
          <TransactionTrackedPrices prices={detailPrices} />
          <ReceiptDetails key={detail.id} transactionId={detail.id} transaction={detail} disabled={detailBusy}
            entryOptions={data?.entryOptions} onBusyChange={setDetailBusy} onChanged={() => void refresh(true)}
            onUseDetails={canEdit && !detail.toAccountId && !detail.iouId ? receipt => void startEdit(false, receipt) : undefined} />
        </>
      )}
      {detailError && (
        <p className="form-error" role="alert">
          {detailError}
        </p>
      )}
      {editingDetail && !detailBusy && <button type="button" className="text-button" onClick={() => setConfirm({ title: "Discard this edit draft?", message: "Saved changes are kept. Only this device’s unfinished draft is removed; reopen Edit to load current values.", confirmLabel: "Discard draft", onConfirm: () => { localStorage.removeItem(editKey(detail.id)); editJournal.current = null; setEditingDetail(false); setDetailError(""); void refresh(true); } })}>Discard draft & reload latest</button>}
    </Modal>
    {makingRecurring && (
      <MakeTransactionRecurring
        transaction={detail}
        onClose={() => setMakingRecurring(false)}
        onCreated={() => refresh(true)}
      />
    )}
    </>
  );
}
